/**
 * TanStack Query layer for the per-user Modeling assumptions draft — the DB-backed
 * persistence behind the `use-modeling-inputs` store (mirrors the segment draft's
 * `useSegmentSync` exactly):
 *
 * - `modelingAssumptionsQueryOptions` — the loader-warmed GET, shared by the Modeling
 *   route loader and the sync hook (one cache entry; the store's pre-bind fallback in
 *   `useModelingInputs` reads the same key for a flash-free first paint).
 * - `useModelingAssumptionsSync` — binds the ACTIVE org into the (org-namespaced)
 *   assumptions store, hydrates the live store from the server draft, then
 *   debounce-autosaves edits back (cross-device), flushing any pending save on
 *   unmount. Hydration + autosave are both scoped to the bound org, so an org switch
 *   can never save one tenant's assumptions into another's draft.
 */
import { useEffect, useRef } from 'react';
import { useMutation, useQuery, type UseMutationResult } from '@tanstack/react-query';
import { useDebouncedValue } from '@mantine/hooks';
import { apiClient } from '@repo/shared/client';
import {
  modelingAssumptionsContract,
  modelingAssumptionsUpsertContract,
  type ModelingAssumptionsResponse,
  type ModelingInputs,
} from '@repo/shared/common';
import { contractQueryOptions } from '~/lib/query';
import { useFlushOnUnmount } from '~/lib/hooks/use-flush-on-unmount';
import { currentOrganizationQueryOptions } from '~/lib/queries/organization';
import { MODELING_ASSUMPTIONS_KEY, useModelingInputsStore } from './use-modeling-inputs';

/** Debounce (ms) for the assumptions autosave — calm, not chatty. */
const ASSUMPTIONS_AUTOSAVE_DEBOUNCE_MS = 400;

/** The per-user assumptions draft options, shared by the route loader and the sync hook. */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- opaque queryOptions brand; the contract keeps callers typed.
export function modelingAssumptionsQueryOptions() {
  return contractQueryOptions(modelingAssumptionsContract, [...MODELING_ASSUMPTIONS_KEY]);
}

function useUpsertModelingAssumptions(): UseMutationResult<
  ModelingAssumptionsResponse,
  Error,
  ModelingInputs
> {
  return useMutation<ModelingAssumptionsResponse, Error, ModelingInputs>({
    mutationFn: (assumptions) =>
      apiClient(modelingAssumptionsUpsertContract, { body: { assumptions } }),
  });
}

/**
 * Reconcile the live assumptions store with the server draft, PER ORGANIZATION
 * (mirrors `useSegmentSync` — see its JSDoc for the full rationale):
 *
 * 1. Bind the active org id into the store (namespaced by org — until the binding
 *    lands it reads the defaults, so another tenant's assumptions never flash).
 * 2. Hydrate ONCE PER ORG from the server draft (the cross-device source of truth) —
 *    but only from a draft response that is FRESH and not refetching, so an org
 *    switch can never hydrate the previous org's cached draft into the new org's
 *    slot. A null draft (nothing saved yet) still completes the hydration cycle:
 *    the store keeps its defaults and autosave arms.
 * 3. Debounce-autosave subsequent edits, keyed to the org they were made under. A
 *    `lastSaved` guard prevents the hydration itself (or an unchanged value) from
 *    triggering a redundant write, and pending edits flush on unmount so navigating
 *    away never drops a change.
 */
export function useModelingAssumptionsSync(): void {
  const orgQuery = useQuery(currentOrganizationQueryOptions());
  const organizationId = orgQuery.data?.organization?.id ?? null;

  const draft = useQuery(modelingAssumptionsQueryOptions());
  const upsert = useUpsertModelingAssumptions();
  const inputs = useModelingInputsStore((state) =>
    state.organizationId === null ? null : state.byOrg[state.organizationId] ?? null,
  );
  const hydrate = useModelingInputsStore((state) => state.hydrate);
  const setOrganization = useModelingInputsStore((state) => state.setOrganization);

  // 1. Org binding — the store no-ops edits and reads defaults until this lands.
  useEffect(() => {
    setOrganization(organizationId);
  }, [organizationId, setOrganization]);

  // Which org we last hydrated + last saved for; an org switch resets the cycle.
  const hydratedOrgRef = useRef<string | null>(null);
  const lastSavedRef = useRef<string | null>(null);
  const upsertRef = useRef(upsert);
  upsertRef.current = upsert;

  // 2. Per-org hydration, gated on a fresh, settled draft response (see JSDoc).
  useEffect(() => {
    if (organizationId === null || hydratedOrgRef.current === organizationId) return;
    if (!draft.data || draft.isFetching || draft.isStale) return;
    if (draft.data.assumptions !== null) {
      hydrate(draft.data.assumptions);
      lastSavedRef.current = JSON.stringify(draft.data.assumptions);
    } else {
      // Nothing saved yet: keep the defaults, but arm autosave with "nothing saved"
      // so the first real edit (or cohort seed) creates the row.
      lastSavedRef.current = null;
    }
    hydratedOrgRef.current = organizationId;
  }, [organizationId, draft.data, draft.isFetching, draft.isStale, hydrate]);

  // 3. Autosave — only after this org hydrated, so a switch never writes cross-tenant.
  const [debounced] = useDebouncedValue(inputs, ASSUMPTIONS_AUTOSAVE_DEBOUNCE_MS);
  useEffect(() => {
    if (organizationId === null || hydratedOrgRef.current !== organizationId) return;
    if (debounced === null) return; // no edit has touched this org's slot yet
    const json = JSON.stringify(debounced);
    if (json === lastSavedRef.current) return;
    lastSavedRef.current = json;
    upsertRef.current.mutate(debounced);
  }, [debounced, organizationId]);

  // Flush a pending (un-debounced) edit when the user navigates away mid-window.
  useFlushOnUnmount(() => {
    if (organizationId === null || hydratedOrgRef.current !== organizationId) return;
    if (inputs === null) return;
    const json = JSON.stringify(inputs);
    if (json === lastSavedRef.current) return;
    lastSavedRef.current = json;
    upsertRef.current.mutate(inputs);
  });
}
